import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import ArquivoService from '../../../services/ArquivosService';
import { errorToast, infoToast } from '../../DommusToast';
import sendOutlined from '@iconify-icons/ant-design/send-outlined';
import './style.scss';

const DURACAO_MAXIMA_SEGUNDOS = 300;

function formatarTempo(segundos) {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${String(minutos).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;
}

export function GravadorAudio({ enviarAudio, inputDesativado, onEstadoChange }) {
    const [estadoGravacao, setEstadoGravacao] = useState('ocioso');
    const [tempoSegundos, setTempoSegundos] = useState(0);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const audioUrlRef = useRef(null);
    const blobRef = useRef(null);
    const mimeTypeRef = useRef(null);
    const intervalRef = useRef(null);
    const containerRef = useRef(null);

    const mudarEstado = useCallback((novoEstado) => {
        setEstadoGravacao(novoEstado);
        if (onEstadoChange) onEstadoChange(novoEstado);
    }, [onEstadoChange]);

    const pararGravacao = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        clearInterval(intervalRef.current);
    }, []);

    const liberarRecursos = useCallback(() => {
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        chunksRef.current = [];
        blobRef.current = null;
        mimeTypeRef.current = null;
        clearInterval(intervalRef.current);
        setTempoSegundos(0);
    }, []);

    useEffect(() => {
        if (estadoGravacao !== 'gravando') return;

        const handleClickFora = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                pararGravacao();
            }
        };

        document.addEventListener('mousedown', handleClickFora);
        return () => {
            document.removeEventListener('mousedown', handleClickFora);
        };
    }, [estadoGravacao, pararGravacao]);

    const handleIniciarGravacao = async () => {
        if (inputDesativado) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chunksRef.current = [];

            const mediaRecorder = new MediaRecorder(stream);
            mimeTypeRef.current = mediaRecorder.mimeType || 'audio/webm';
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
                blobRef.current = blob;
                if (audioUrlRef.current) {
                    URL.revokeObjectURL(audioUrlRef.current);
                }
                audioUrlRef.current = URL.createObjectURL(blob);
                stream.getTracks().forEach(track => track.stop());
                mudarEstado('preview');
            };

            mediaRecorder.start();
            setTempoSegundos(0);
            mudarEstado('gravando');

            intervalRef.current = setInterval(() => {
                setTempoSegundos(prev => {
                    const proximo = prev + 1;
                    if (proximo >= DURACAO_MAXIMA_SEGUNDOS) {
                        pararGravacao();
                        infoToast.fire({ text: 'Tempo máximo de gravação atingido (5 minutos).' });
                        clearInterval(intervalRef.current);
                    }
                    return proximo;
                });
            }, 1000);
        } catch (err) {
            errorToast.fire({ text: 'Permissão do microfone negada. Habilite o acesso ao microfone no navegador.' });
        }
    };

    const handlePararGravacao = () => {
        pararGravacao();
    };

    const handleEnviarAudio = async () => {
        if (!blobRef.current) return;
        try {
            // Converter para WAV (formato universal) para garantir compatibilidade com iOS
            const wavBlob = await ArquivoService.converterBlobParaWav(blobRef.current);
            const base64 = await ArquivoService.converterBlobParaBase64(wavBlob);
            enviarAudio(base64, 'audio/wav');
        } catch (err) {
            // Fallback: enviar no formato original se a conversão falhar
            try {
                const base64 = await ArquivoService.converterBlobParaBase64(blobRef.current);
                enviarAudio(base64, mimeTypeRef.current);
            } catch (fallbackErr) {
                errorToast.fire({ text: 'Erro ao processar o áudio para envio.' });
            }
        }
        liberarRecursos();
        mudarEstado('ocioso');
    };

    const handleDescartar = () => {
        liberarRecursos();
        mudarEstado('ocioso');
    };

    if (estadoGravacao === 'ocioso') {
        return (
            <Button
                className="btn-microfone"
                disabled={inputDesativado}
                onClick={handleIniciarGravacao}
                title="Gravar áudio"
            >
                <Icon icon="mdi:microphone" color="white" />
            </Button>
        );
    }

    if (estadoGravacao === 'gravando') {
        return (
            <div className="container-gravando" ref={containerRef}>
                <span className="indicador-gravando" />
                <span className="timer-gravacao">{formatarTempo(tempoSegundos)}</span>
                <Button className="btn-stop-gravacao" onClick={handlePararGravacao} title="Parar gravação">
                    <Icon icon="mdi:stop" />
                </Button>
            </div>
        );
    }

    if (estadoGravacao === 'preview') {
        return (
            <div className="container-preview-audio">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio controls src={audioUrlRef.current} className="player-audio-gravado" />
                <div className="grupo-acoes-audio">
                    <Button className="btn-descartar-audio" variant="danger" onClick={handleDescartar} title="Descartar gravação">
                        <Icon icon="mdi:close" color="white" />
                    </Button>
                    <Button className="btn-enviar-audio" onClick={handleEnviarAudio} title="Enviar áudio">
                        <Icon icon={sendOutlined} color="white" />
                    </Button>
                </div>
            </div>
        );
    }

    return null;
}
